import { createEffectScope, ref } from 'fine-jsx'
import * as Vue from 'vue'

import type * as pub from '#core'
import type { Schema } from '#core'
import type { Valueof } from '#internal'

import { DocumentView, type ViewType } from '../document-view.ts'
import { defineWrapperProps } from '../utils.ts'

import { ClipDragContext } from './clip-drag-context.ts'
import { ClipResizeContext } from './clip-resize-context.ts'
import { EditNodeLink } from './edit-node-link.ts'
import { EditClip, EditView } from './edit-nodes.ts'

export interface ViewTypeMap {
  timeline: EditView.Timeline
  'track:video': EditView.VideoTrack
  'track:audio': EditView.VideoTrack
  'clip:video': EditView.VideoClip
  'clip:audio': EditView.AudioClip
  'clip:image': EditView.ImageClip
  'clip:text': EditView.TextClip
}

/**
 * This {@link DocumentView} wraps the original document and timeline nodes, adding methods that let us edit
 * node properties and preview the changes without syncing or being interrupted by external changes to the
 * same prop.
 */
export class EditDocument extends DocumentView<ViewTypeMap> implements pub.Document {
  readonly vueScope = Vue.effectScope()
  readonly fineJsxScope = createEffectScope()
  readonly edits = new Map<pub.AnyNode, pub.Schema.AnyNode>()

  declare resolution: pub.Document['resolution']
  declare frameRate: pub.Document['frameRate']
  declare readonly currentTime: pub.Document['currentTime']
  declare readonly _currentFrameTime: pub.Document['_currentFrameTime']
  declare readonly duration: pub.Document['duration']
  declare readonly timeline: pub.Document['timeline']
  declare readonly assets: pub.Document['assets']
  declare readonly isEmpty: pub.Document['isEmpty']
  /** @internal */
  declare readonly activeClipIsStalled: pub.Document['activeClipIsStalled']

  readonly #eventTarget = new EventTarget()
  readonly ownEvents = new WeakSet<Event>()

  readonly nodes = {
    get: <T extends pub.AnyNode>(id: string) =>
      this._getNode(this.doc.nodes.get(id)) as unknown as T & EditView<T>,
    set: () => {
      throw new Error('Unreachable?')
    },
    has: this.doc.nodes.has.bind(this.doc.nodes),
    delete: this.doc.nodes.delete.bind(this.doc.nodes),
    forEach: this.doc.nodes.forEach,
  }

  readonly #links = ref(new Map<string, EditNodeLink>())

  get links() {
    return this.#links.value
  }

  seekTo = this.doc.seekTo.bind(this.doc)
  _setCurrentTime = this.doc._setCurrentTime.bind(this.doc)
  importFromJson = this.doc.importFromJson.bind(this.doc)
  toJSON = this.doc.toJSON.bind(this.doc)
  emit = this.doc.emit.bind(this.doc)

  readonly clipDrag = new ClipDragContext()
  readonly clipResize = new ClipResizeContext()

  constructor(doc: pub.Document) {
    super(doc)

    const listenerOptions: AddEventListenerOptions = { signal: this._abort.signal }
    const onEventWithNode = this.#onEventWithNode.bind(this)
    const onEventWithLink = this.#onEventWithLink.bind(this)

    // must be before the delete listener is added in this._init()
    this.doc.on('node:delete', onEventWithNode, listenerOptions)

    this._init()
    ;(
      [
        'node:create',
        'node:move',
        'node:update',
        'node:gap-update',
        'node:list-change',
        'canvas:pointerdown',
        'canvas:pointermove',
        'canvas:pointerup',
      ] satisfies (keyof pub.VideoEditorEvents)[]
    ).forEach((type) => void this.doc.on(type, onEventWithNode, listenerOptions))
    ;(['link:create', 'link:update', 'link:delete'] satisfies (keyof pub.VideoEditorEvents)[]).forEach(
      (type) => void this.doc.on(type, onEventWithLink, listenerOptions),
    )

    this.timeline = this._getNode(doc.timeline)
    doc.links.forEach((init) => void this.links.set(init.id, new EditNodeLink(this, init)))
  }

  protected _createView<T extends pub.AnyNode>(original: T): ViewType<ViewTypeMap, T> {
    const view = original.isClip() ? new EditClip(this, original) : new EditView(this, original)
    const proxy = EditView.proxy(view)
    return proxy as unknown as ViewType<ViewTypeMap, T>
  }

  _getNode<T extends undefined>(original: T): T
  _getNode<T extends pub.AnyNode>(original: T): ViewType<ViewTypeMap, T>
  _getNode<T extends pub.AnyNode | undefined>(original: T): ViewType<ViewTypeMap, T>
  _getNode<T extends pub.AnyNode | undefined>(original: T): ViewType<ViewTypeMap, T> | undefined {
    return original?._is_edit_view
      ? (original as unknown as ViewType<ViewTypeMap, T>)
      : super._getNode(original)
  }

  createNode<T extends pub.Schema.AnyNode>(init: T): pub.NodesByType[T['type']] {
    const node = this.doc.createNode(init)
    return (this._createView(node) as typeof node | undefined) ?? node
  }

  #onEventWithNode(
    event: pub.VideoEditorEvents[Extract<keyof pub.VideoEditorEvents, `node:${string}` | `canvas:${string}`>],
  ) {
    const { node } = event
    const view = node && this._getNode(node)

    if (!view) {
      this.#eventTarget.dispatchEvent(event.clone())
      return
    }

    let newEvent

    switch (event.type) {
      case 'node:create':
        newEvent = event.clone(view)
        break
      case 'node:move':
        newEvent = event.clone(view, event.from)
        break
      case 'node:update':
      case 'node:gap-update': {
        const editProps = view._editedProps.value

        const propKey = event.type === 'node:gap-update' ? 'gap' : event.key
        // ignore updates while editing
        if (editProps && !view._isEnding && propKey in editProps) return
        newEvent = event.clone(view as any)
        break
      }
      case 'node:delete':
        newEvent = event.clone(view)
        break
      default:
        newEvent = event.clone(view as any)
    }

    this.#eventTarget.dispatchEvent(newEvent)
  }

  createLink(init: pub.Schema.NodeLink): EditNodeLink {
    this.doc.createLink(init)
    return this.links.get(init.id)!
  }

  updateLink(id: string, nodes: Schema.NodeLink['nodes']): void {
    this.doc.updateLink(id, nodes)
  }

  deleteLink(id: string): void {
    this.doc.deleteLink(id)
  }

  #onEventWithLink(event: pub.VideoEditorEvents[Extract<keyof pub.VideoEditorEvents, `link:${string}`>]) {
    const linkInit = event.link
    const { id } = linkInit

    let newEvent

    switch (event.type) {
      case 'link:create': {
        const link = new EditNodeLink(this, linkInit)
        const map = (this.#links.value = new Map(this.links))
        map.set(id, link)

        newEvent = event.clone(link)
        break
      }
      case 'link:update': {
        const link = this.links.get(id)
        if (!link) return
        newEvent = event.clone(link)
        break
      }
      case 'link:delete': {
        const map = (this.#links.value = new Map(this.links))
        const link = map.get(id)
        if (!link) return

        newEvent = event.clone(link)
        link.dispose()
        map.delete(id)
        break
      }
    }

    this.#eventTarget.dispatchEvent(newEvent)
  }

  on(
    type: keyof pub.VideoEditorEvents,
    listener: (event: Valueof<pub.VideoEditorEvents>) => void,
    options_?: AddEventListenerOptions,
  ) {
    const options: AddEventListenerOptions = { signal: this._abort.signal, ...options_ }

    if (type.startsWith('node:') || type.startsWith('canvas:')) {
      this.#eventTarget.addEventListener(type, listener, options)
      return () => this.#eventTarget.removeEventListener(type, listener, options)
    }

    return this.doc.on(type, listener, options)
  }

  _emit(event: pub.VideoEditorEvents[keyof pub.VideoEditorEvents]): void {
    this.#eventTarget.dispatchEvent(event)
  }

  dispose(): void {
    super.dispose()
  }
}

defineWrapperProps(EditDocument, 'doc', [
  'resolution',
  'frameRate',
  'currentTime',
  '_currentFrameTime',
  'duration',
  'timeline',
  'assets',
  'isEmpty',
  'activeClipIsStalled',
])
